import moment from "moment";
import "moment/locale/es";

/**
 * Builds a compact text summary of parking data for the AI assistant.
 * Keeps token count low while providing enough context to answer questions.
 */
const buildDataSummary = (dailyDocs, monthlyDocs, liveData) => {
  const lines = [];

  // ── Live data ─────────────────────────────────────────────
  if (liveData) {
    lines.push("=== DATOS EN TIEMPO REAL ===");
    if (liveData.occupancy != null) {
      lines.push(`Ocupacion: ${liveData.occupancy.current || 0}/${liveData.occupancy.capacity || "?"} lugares`);
    }
    if (liveData.today) {
      const t = liveData.today;
      lines.push(`Hoy: ingresos=$${t.income || 0}, egresos=$${t.expense || 0}, entradas=${t.tickets_in || 0}, salidas=${t.tickets_out || 0}`);
    }
    lines.push("");
  }

  // ── Monthly summary ───────────────────────────────────────
  if (monthlyDocs.length > 0) {
    lines.push("=== RESUMEN MENSUAL ===");
    const sorted = [...monthlyDocs].sort((a, b) => b.month.localeCompare(a.month));
    sorted.forEach((m) => {
      const label = moment(m.month, "YYYY-MM").format("MMMM YYYY");
      const totalIn = m.tickets?.total?.in || 0;
      const totalOut = m.tickets?.total?.out || 0;
      const avgTicket = totalOut > 0 ? Math.round((m.tickets?.total?.amount || 0) / totalOut) : 0;
      let line = `${label}: ingresos=$${m.income || 0}, egresos=$${m.expense || 0}, entradas=${totalIn}, salidas=${totalOut}, ticket_prom=$${avgTicket}`;
      if (m.best_day) line += `, mejor_dia=${m.best_day.date}($${m.best_day.income})`;
      if (m.worst_day) line += `, peor_dia=${m.worst_day.date}($${m.worst_day.income})`;
      if (m.averages) {
        line += `, prom_diario=$${Math.round(m.averages.daily_income || 0)}, tickets_dia=${Math.round(m.averages.daily_tickets_in || 0)}`;
      }
      lines.push(line);
    });
    lines.push("");
  }

  // ── Daily summary (last 30 days detailed, older aggregated) ──
  if (dailyDocs.length > 0) {
    const sorted = [...dailyDocs].sort((a, b) => b.date.localeCompare(a.date));
    const recent = sorted.slice(0, 30);
    const older = sorted.slice(30);

    lines.push("=== ULTIMOS 30 DIAS (detalle diario) ===");
    recent.forEach((d) => {
      const dow = moment(d.date).format("ddd");
      const totalIn = d.tickets?.total?.in || 0;
      const totalOut = d.tickets?.total?.out || 0;
      const canceled = d.canceled_count || 0;
      let line = `${d.date} (${dow}): ingreso=$${d.income || 0}, egreso=$${d.expense || 0}, ent=${totalIn}, sal=${totalOut}`;
      if (canceled > 0) line += `, cancel=${canceled}`;

      // Payment methods if available
      // Las claves son los NOMBRES de las formas de pago que configura cada
      // cliente ("Efectivo", "Tarjeta de Debito", ...), no un set fijo. Antes se
      // buscaban claves en ingles que no existian nunca: el asistente jamas veia
      // el desglose por medio de pago.
      if (d.payments && typeof d.payments === "object") {
        const methods = Object.entries(d.payments)
          .filter(([, v]) => v)
          .map(([k, v]) => `${k}=$${v}`);
        if (methods.length) line += ` [${methods.join(", ")}]`;
      }

      // Ticket types
      const types = [];
      if (d.tickets?.hour?.in) types.push(`hora:${d.tickets.hour.in}($${d.tickets.hour.amount || 0})`);
      if (d.tickets?.stay?.in) types.push(`estadia:${d.tickets.stay.in}($${d.tickets.stay.amount || 0})`);
      if (d.tickets?.tn?.in) types.push(`turno:${d.tickets.tn.in}($${d.tickets.tn.amount || 0})`);
      if (d.tickets?.plana?.in) types.push(`plana:${d.tickets.plana.in}($${d.tickets.plana.amount || 0})`);
      if (types.length > 1) line += ` tipos:[${types.join(", ")}]`;

      // Monthly/deals income
      // El emisor manda `payments`, no `count`: antes decia "(0)" con el monto correcto al lado.
      if (d.monthly?.amount) line += `, mensual=$${d.monthly.amount}(${d.monthly.payments || 0})`;
      if (d.deals?.amount) line += `, acuerdos=$${d.deals.amount}`;

      lines.push(line);
    });

    if (older.length > 0) {
      lines.push("");
      lines.push(`=== DIAS ANTERIORES (${older.length} dias agregados) ===`);
      const totalIncome = older.reduce((a, d) => a + (d.income || 0), 0);
      const totalExpense = older.reduce((a, d) => a + (d.expense || 0), 0);
      const totalTicketsIn = older.reduce((a, d) => a + (d.tickets?.total?.in || 0), 0);
      const totalCanceled = older.reduce((a, d) => a + (d.canceled_count || 0), 0);
      lines.push(`Total: ingresos=$${totalIncome}, egresos=$${totalExpense}, entradas=${totalTicketsIn}, cancelados=${totalCanceled}`);
      lines.push(`Promedio diario: ingreso=$${Math.round(totalIncome / older.length)}, entradas=${Math.round(totalTicketsIn / older.length)}`);
      lines.push(`Rango: ${older[older.length - 1].date} a ${older[0].date}`);
    }
  }

  return lines.join("\n");
};

export default buildDataSummary;
